import React from 'react';
import SectionHeader from './SectionHeader';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '../../components/carousel';
import { cn } from '@univdiam/ui/lib/utils';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileArchive,
  FaFileAlt,
  FaDownload,
  FaFileVideo
} from 'react-icons/fa';
import { normalizeUrl } from '@univdiam/ui/lib/UrlNormalize';
import { Paperclip } from 'lucide-react';

type AttachmentsProps = {
  attachmentLinks: string[];
  isAdmin: boolean;
};

function Attachments({ attachmentLinks, isAdmin }: AttachmentsProps) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [pageCount, setPageCount] = React.useState(0);

  // media_attachment may arrive as a JSON string from the API — ensure it's a proper array
  const parsedLinks: string[] = React.useMemo(() => {
    if (!attachmentLinks) return [];
    if (Array.isArray(attachmentLinks)) return attachmentLinks;
    if (typeof attachmentLinks === 'string') {
      try {
        const parsed = JSON.parse(attachmentLinks);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, [attachmentLinks]);

  React.useEffect(() => {
    if (!api) return;
    setPageCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on('select', () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  const getFileType = (url: string) => {
    // Strip query params and hash before extracting extension
    const cleanUrl = url.split('?')[0]!.split('#')[0]!;
    // Only consider the last path segment for extension detection
    const lastSegment = cleanUrl.split('/').pop() || '';
    const extension = lastSegment.includes('.') ? lastSegment.split('.').pop()?.toLowerCase() : undefined;
    if (!extension) return 'unknown';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension))
      return 'image';
    if (['pdf'].includes(extension)) return 'pdf';
    if (['xls', 'xlsx', 'csv'].includes(extension)) return 'excel';
    if (['ppt', 'pptx'].includes(extension)) return 'powerpoint';
    if (['doc', 'docx'].includes(extension)) return 'word';
    if (['zip', 'rar'].includes(extension)) return 'zip';
    if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv'].includes(extension))
      return 'video';
    return 'unknown';
  };

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return <FaFilePdf className="text-red-500" />;
    if (type.includes('word')) return <FaFileWord className="text-blue-600" />;
    if (type.includes('excel')) return <FaFileExcel className="text-green-600" />;
    if (type.includes('powerpoint')) return <FaFilePowerpoint className="text-orange-500" />;
    if (type.includes('zip')) return <FaFileArchive className="text-yellow-600" />;
    if (type.includes('video')) return <FaFileVideo className="text-purple-500" />;
    return <FaFileAlt className="text-gray-500" />;
  };

  const handleDownload = async (url: string) => {
    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'video/mp4,video/*,*/*',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      const contentType = response.headers.get('content-type')

      // Determine file extension from content type or URL
      let extension = 'mp4'
      if (contentType?.includes('webm')) extension = 'webm'
      if (contentType?.includes('quicktime')) extension = 'mov'
      if (contentType?.includes('x-m4v')) extension = 'm4v'

      const fileName = 'UnivDiam_' + (url.split('/').pop()?.split('?')[0] || `video.${extension}`)

      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)

    } catch (err) {
      console.error('Download failed:', err)
    }
  };

  // Track which attachments failed to load as images
  const [failedImages, setFailedImages] = React.useState<Set<number>>(new Set());

  const handleImageError = (index: number) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  return (
    <div className="flex flex-col bg-white border border-[#E9EAEB] rounded-md text-caption1 overflow-hidden">
      <SectionHeader
        title="Attachments"
        sectionIcon={<Paperclip strokeWidth={1.5} className="w-5 h-5 text-gray-600" />}
        isAdmin={isAdmin}
      />

      <div className="flex-1 p-0 pb-2">
        <div className="px-4 mt-4">
          <Carousel opts={{ align: 'start' }} setApi={setApi} className="w-full">
            <CarouselContent>
              {parsedLinks?.map((attachment, index) => {
                const type = getFileType(attachment);
                // Render as image if type is 'image' OR type is 'unknown' (try image first with fallback)
                const shouldRenderAsImage = (type === 'image' || type === 'unknown') && !failedImages.has(index);

                return (
                  <CarouselItem
                    key={index}
                    className="basis-full md:basis-1/2 lg:basis-1/3"
                  >
                    <div className="overflow-hidden shadow-none border-none group relative">
                      <a href={normalizeUrl(attachment)} target="_blank" rel="noopener noreferrer">
                        <div className="p-0 h-full flex flex-col items-center justify-center relative cursor-pointer">
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              handleDownload(attachment)
                            }}
                            className="absolute top-2 right-2 z-10 p-2 rounded-full bg-white/90 backdrop-blur-sm border border-gray-500 shadow-lg text-gray-700 opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-110 transition-all duration-200 cursor-pointer"
                          >
                            <FaDownload size={16} />
                          </button>

                          {shouldRenderAsImage ? (

                            <img
                              src={normalizeUrl(attachment)}
                              alt={`Attachment ${index + 1}`}
                              className="w-full object-cover rounded-md border border-[#E9EAEB] h-[214px]"
                              onError={() => handleImageError(index)}
                            />
                          ) : (

                            <div
                              className={cn(
                                'w-full object-cover rounded-md border border-[#E9EAEB] h-[214px] text-[40px] flex flex-col items-center justify-center bg-gray-50 text-gray-600',
                              )}
                            >
                              {getFileIcon(type)}
                            </div>
                          )}
                        </div>
                      </a>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>

            <div className="flex items-center justify-between pb-4 mt-4">
              <div className="flex items-center gap-2">
                <CarouselPrevious className="relative top-0 translate-y-0 left-0" />
                <CarouselNext className="relative top-0 translate-y-0 left-0" />
              </div>
              <div className="flex items-center gap-2">
                {Array.from({ length: pageCount })?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => api?.scrollTo(index)}
                    className={cn('h-3.5 w-3.5 rounded-full border-2 transition-colors', {
                      'border-primary bg-primary': current === index,
                      'border-gray-300 hover:border-gray-400': current !== index,
                    })}
                  />
                ))}
              </div>
            </div>
          </Carousel>
        </div>
      </div>
    </div>
  );
}

export default Attachments;